import type { DeepPartial } from '@gitbook/util-ts';

import type { NavigationScreenSpaceState, NavigationScreenState } from './types';

/**
 * Set the default mode for a space screen if it's not already set.
 * For CR screens, 'overview' is the default (but not shown in URL).
 * For non-CR screens, 'edit' is the default (but not shown in URL).
 */
export function ensureSpaceScreenMode(
    screen: NavigationScreenSpaceState | DeepPartial<NavigationScreenSpaceState>
): void {
    if (screen.mode === undefined) {
        const isChangeRequest = 'changeRequest' in screen && screen.changeRequest !== undefined;
        screen.mode = isChangeRequest ? 'overview' : 'edit';
    }
}
/**
 * Normalize a screen to avoid invalid state.
 * It throws an error if properties are missing.
 *
 * example: when current screen is { type: 'collection', collection: 'something' }
 * and a navigation call is done with { type: 'space', space: 'something' }
 * which miss a "page", etc
 */
export function normalizeNavigationScreen(
    screen: DeepPartial<NavigationScreenState>
): NavigationScreenState {
    switch (screen.type) {
        case 'space': {
            const { space } = screen;
            if (!space) {
                throw new Error('Navigation screen space is required');
            }

            ensureSpaceScreenMode(screen);

            // @ts-ignore - we know that the screen is valid
            return {
                type: 'space',
                page: '',
                space,
                ...screen,
            };
        }
        case 'collection': {
            const { collection } = screen;
            if (!collection) {
                throw new Error('Navigation screen collection is required');
            }

            // @ts-ignore - we know that the screen is valid
            return {
                type: 'collection',
                collection,
                ...screen,
            };
        }
        default:
            // @ts-ignore no normalization implemented yet, let's consider the screen is valid
            return screen;
    }
}
